import { useMemo } from 'react'

import { __TEST__ } from 'core/infra/environment'
import { HttpClient } from 'core/infra/http/HttpClient'
import { IndexedDBKeyValueStore } from 'core/infra/keyvalue/IndexedDBKeyValueStore'
import { MemoryKeyValueStore } from 'core/infra/keyvalue/MemoryKeyValueStore'

import { type LaneHistory } from '../../domain/LaneHistory'
import { type SuggestedPreferredLane } from '../../domain/SuggestedPreferredLane'
import { LaneHistoryRepository } from '../../infra/LaneHistoryRepository'
import { PreferredLaneRepository } from '../../infra/PreferredLaneRepository'
import { SuggestedPreferredLaneRepository } from '../../infra/SuggestedPreferredLaneRepository'
import { addBidToLaneHistoryFactory } from '../../use-cases/addBidToLaneHistory'
import { addBookToLaneHistoryFactory } from '../../use-cases/addBookToLaneHistory'
import { addSearchToLaneHistoryFactory } from '../../use-cases/addSearchToLaneHistory'
import { dismissSuggestedPreferredLaneFactory } from '../../use-cases/dismissSuggestedPreferredLane'
import { getSuggestedPreferredLanesFactory } from '../../use-cases/getSuggestedPreferredLanes'
import { shouldSuggestPreferredLaneOnBidFactory } from '../../use-cases/shouldSuggestPreferredLaneOnBid'
import { shouldSuggestPreferredLaneOnBookFactory } from '../../use-cases/shouldSuggestPreferredLaneOnBook'
import { shouldSuggestPreferredLaneOnSearchFactory } from '../../use-cases/shouldSuggestPreferredLaneOnSearch'
import { suggestPreferredLaneFactory } from '../../use-cases/suggestPreferredLane'
import { trackLaneBidFactory } from '../../use-cases/trackLaneBid'
import { trackLaneBookFactory } from '../../use-cases/trackLaneBook'
import { trackLaneSearchFactory } from '../../use-cases/trackLaneSearch'

/** Refactor: We had to use the MemoryKeyValueStore for the tests to pass */
const KeyValueStore = __TEST__ ? MemoryKeyValueStore : IndexedDBKeyValueStore

export function useSuggestedPreferredLanes() {
  return useMemo(() => {
    const httpClient = new HttpClient(process.env.REACT_APP_API_URL)

    const laneHistoryKeyValueStore = new KeyValueStore<LaneHistory>('PreferredLane_LaneHistory')
    const suggestedPreferredLaneKeyValueStore = new KeyValueStore<SuggestedPreferredLane>(
      'PreferredLane_SuggestedPreferredLane'
    )

    const laneHistoryRepository = new LaneHistoryRepository({
      laneHistoryKeyValueStore,
    })
    const preferredLaneRepository = new PreferredLaneRepository({
      httpClient,
    })
    const suggestedPreferredLaneRepository = new SuggestedPreferredLaneRepository({
      suggestedPreferredLaneKeyValueStore,
    })

    const addBidToLaneHistory = addBidToLaneHistoryFactory({
      laneHistoryRepository,
    })
    const addBookToLaneHistory = addBookToLaneHistoryFactory({
      laneHistoryRepository,
    })
    const addSearchToLaneHistory = addSearchToLaneHistoryFactory({
      laneHistoryRepository,
    })
    const dismissSuggestedPreferredLane = dismissSuggestedPreferredLaneFactory({
      suggestedPreferredLaneRepository,
    })
    const getSuggestedPreferredLanes = getSuggestedPreferredLanesFactory({
      suggestedPreferredLaneRepository,
    })
    const shouldSuggestPreferredLaneOnBid = shouldSuggestPreferredLaneOnBidFactory({
      laneHistoryRepository,
      preferredLaneRepository,
      suggestedPreferredLaneRepository,
    })
    const shouldSuggestPreferredLaneOnBook = shouldSuggestPreferredLaneOnBookFactory({
      laneHistoryRepository,
      preferredLaneRepository,
      suggestedPreferredLaneRepository,
    })
    const shouldSuggestPreferredLaneOnSearch = shouldSuggestPreferredLaneOnSearchFactory({
      laneHistoryRepository,
      preferredLaneRepository,
      suggestedPreferredLaneRepository,
    })
    const suggestPreferredLane = suggestPreferredLaneFactory({
      laneHistoryRepository,
      suggestedPreferredLaneRepository,
    })
    const trackLaneBid = trackLaneBidFactory({
      laneHistoryRepository,
      preferredLaneRepository,
      suggestedPreferredLaneRepository,
    })
    const trackLaneBook = trackLaneBookFactory({
      laneHistoryRepository,
      preferredLaneRepository,
      suggestedPreferredLaneRepository,
    })
    const trackLaneSearch = trackLaneSearchFactory({
      laneHistoryRepository,
      preferredLaneRepository,
      suggestedPreferredLaneRepository,
    })

    return {
      addBidToLaneHistory,
      addBookToLaneHistory,
      addSearchToLaneHistory,
      dismissSuggestedPreferredLane,
      getSuggestedPreferredLanes,
      shouldSuggestPreferredLaneOnBid,
      shouldSuggestPreferredLaneOnBook,
      shouldSuggestPreferredLaneOnSearch,
      suggestPreferredLane,
      trackLaneBid,
      trackLaneBook,
      trackLaneSearch,
    }
  }, [])
}
